
import { MEMORY_CAPACITY_PARAM_SLUG } from './constants';
interface IBadge {
  title: string,
  content: string,
  variant?: string,
  textColor?: string
}


export interface IDeviceCardResponse {
  id: string,
  highlight: string | null,
  isPreorder: boolean,
  isOutOfStock: boolean,
  brandName: string,
  productPromotion: {
    id: string,
  }
  urlSlug: string,
  outOfStockMessageTemplate: string,
  outOfStockReasonSlug: string,
  name: string,
  badges: IBadge[]
  article: string,
  images: string[],
  mainCollectionSlug?: string,
  promotion: {
    available: {
      name: string,
      isOnlyWithPromo: boolean,
      valuePreposition: boolean,
      bundlePrice: number,
      discountPercent: number,
    }
  },
  price: {
    discountPercent: number,
    oldValue: number,
    priceComment: string,
    value: number,
  },
  promotionBundle: {
    state: string,
    isOnlyWithPromo: boolean,
    valuePreposition: string,
    bundlePrice: number,
  }
  mainCollectionName?: string,
  parameters: DeviceParamsType[],
}

interface IPromoBadge {
  children: string,
  help: string,
  variant?: string,
  textColor?: string
}

export interface IDeviceCardBuilder {
  id: string,
  highlight: string | null,
  name: string,
  brandName: string,
  link: string,
  promoBadges: IPromoBadge[],
  promotionId: string,
  outOfStockMessageTemplate: string,
  outOfStockReasonSlug: string,
  isInBasket: boolean,
  slug: string,
  brand: string,
  article: string,
  isPreorder: boolean,
  imgOptions: {
    src: string,
  }[]
  isOutOfStock: boolean,
  price: number,
  oldPrice: number | undefined,
  discount: number,
  isFavorite: boolean,
  isCmp: boolean,
  mainCollectionSlug?: string;
  mainCollectionName?: string;
  variant?: string;
}

type DeviceParamsType = {
  filterKind: number;
  hint: string;
  id: string;
  intagSlug: string;
  intagWeight: number;
  isColor: boolean;
  isIndexing: boolean;
  isMultiple: boolean;
  isUnlimited: boolean;
  name: string;
  productId: number;
  value: string;
};

/**
 * TODO
 * Данный класс используется для маппинга данных в нужный
 * формат для компонента Device из пакета @beef/smart-kit
 * На совместной встрече с командой Нонтелко было выработано решение создать
 * общий промежуточный слой для маппинга данных всех запросов.
 * Поэтому данный код нужно будет перенести в промежуточный слой
 * @deprecated будет удален после выполнения задачи https://servicedesk.veon.com/browse/ES-4517
 */
export class DeviceCardBuilder {
  data = {} as IDeviceCardBuilder;

  setDeviceId(device: IDeviceCardResponse) {
    this.data.id = device.id;

    return this;
  }

  setDeviceHightlights(device: IDeviceCardResponse) {
    this.data.highlight = device?.highlight || null;

    return this;
  }

  setDeviceArticle(device: IDeviceCardResponse) {
    this.data.article = device.article;

    return this;
  }

  setDeviceBrandName(device: IDeviceCardResponse) {
    this.data.brandName = device.brandName;
  }

  setDevicePromotionId(device: IDeviceCardResponse) {
    this.data.promotionId = device?.productPromotion?.id ?? null;

    return this;
  }

  setDeviceSlug(device: IDeviceCardResponse) {
    this.data.slug = device.urlSlug;

    return this;
  }

  setDeviceName(device: IDeviceCardResponse) {
    this.data.name = device.name;

    return this;
  }

  setDeviceDiscount(device: IDeviceCardResponse) {
    this.data.discount = device?.price?.discountPercent;

    return this;
  }

  setDeviceLink(device: IDeviceCardResponse) {
    this.data.link = `/shop/details/${device.urlSlug}`;

    return this;
  }

  setDeviceIsPreorder(device: IDeviceCardResponse) {
    this.data.isPreorder = device.isPreorder;

    return this;
  }

  setDeviceIsOutOfStock(device: IDeviceCardResponse) {
    this.data.isOutOfStock = device.isOutOfStock;
    if (device.isOutOfStock) {
      this.data.outOfStockMessageTemplate = device.outOfStockMessageTemplate;
      this.data.outOfStockReasonSlug = device.outOfStockReasonSlug;
    }

    return this;
  }

  setDeviceIsInBasket(device: IDeviceCardResponse, basketProductIds: string[]) {
    const basketProductIdsSet = new Set(basketProductIds);

    this.data.isInBasket = basketProductIdsSet.has(device.id.toString());

    return this;
  }

  setDeviceIsFavorite(device: IDeviceCardResponse, favoriteList: string[]) {
    const favoriteListSet = new Set(favoriteList);

    this.data.isFavorite = favoriteListSet.has(device.id);

    return this;
  }

  setDeviceIsCmp(device: IDeviceCardResponse, cmpList: string[]) {
    const cmpListSet = new Set(cmpList);

    this.data.isCmp = cmpListSet.has(device.id);

    return this;
  }

  setDeviceImgOptions(device: IDeviceCardResponse) {
    const imgOptions = device?.images
      .map((image: string) => ({ src: image }))
      .slice(0, 6);

    this.data.imgOptions = imgOptions;

    return this;
  }

  setDevicePromoBadges(device: IDeviceCardResponse) {
    const promoBadges = device.badges.reduce((acc: IPromoBadge[], cur: IBadge) => {
      let badge = {
        children: cur.title,
        help: cur.content,
      } as IPromoBadge;

      if (cur.variant) {
        badge = {
          ...badge,
          variant: cur.variant,
        };
      }

      if (cur.textColor) {
        badge = {
          ...badge,
          textColor: cur.textColor,
        };
      }

      acc.push(badge);
      return acc;
    }, []);

    this.data.promoBadges = promoBadges;

    return this;
  }

  // https://servicedesk.veon.com/browse/ES-554 корректное отображение цены
  setDevicePrice(device: IDeviceCardResponse) {
    const isBundleState = !!device?.promotionBundle?.state;
    const isPromo = device?.promotionBundle?.isOnlyWithPromo;
    const isValuePreposition = device?.promotionBundle?.valuePreposition;
    const isBundlePrice = isBundleState && !isValuePreposition && isPromo;

    let price = device?.price?.value;

    if (isValuePreposition || isBundlePrice) {
      price = device?.promotionBundle?.bundlePrice;
    }

    this.data.price = price;

    return this;
  }

  // https://servicedesk.veon.com/browse/ES-554 корректное отображение цены
  setDeviceOldPrice(device: IDeviceCardResponse) {
    const isBundleState = !!device?.promotionBundle?.state;
    const isPromo = device?.promotionBundle?.isOnlyWithPromo;
    const isValuePreposition = device?.promotionBundle?.valuePreposition;
    const isBundlePrice = isBundleState && !isValuePreposition && isPromo;

    let price = device?.price?.value;
    const oldPrice = device?.price?.oldValue;

    if (isValuePreposition || isBundlePrice) {
      price = device?.promotionBundle?.bundlePrice;
    }

    const oldPriceValue = oldPrice === price ? undefined : oldPrice;

    this.data.oldPrice = oldPriceValue;

    return this;
  }

  setMainCollectionSlug(device: IDeviceCardResponse) {
    this.data.mainCollectionSlug = device?.mainCollectionSlug;
  }

  setDeviceMainCollectionName(device: IDeviceCardResponse) {
    this.data.mainCollectionName = device?.mainCollectionName;
  }

  setDeviceVariant(device: IDeviceCardResponse) {
    const memory = device?.parameters?.find((param: DeviceParamsType) =>
      param?.intagSlug === MEMORY_CAPACITY_PARAM_SLUG)?.value || '';
    const variant = `_${memory}`;
    this.data.variant = variant;
  }

  getDeviceData() {
    return this.data;
  }
}

